import {
  MessageBoxStyles,
  MessageBoxVariables,
  MessageBoxWidgetDto,
  ProgressBarStyles,
  ProgressBarVariables,
  ProgressBarWidgetDto,
  QuantityButtonsStyles,
  QuantityButtonsVariables,
  QuantityButtonsVariant,
  QuantityButtonsWidgetDto,
  RecursivePartial,
} from "@intelligems/ig-types";

export const generatePlaceholderPreviewQuantityVariables = (
  buttonVariables: RecursivePartial<QuantityButtonsVariables> | undefined,
  variant: QuantityButtonsVariant
): RecursivePartial<QuantityButtonsVariables> => {
  return {
    ...buttonVariables,
    sectionTitle: "Section title",
    buttons: (buttonVariables?.buttons || []).map((button, index) => ({
      ...button,
      buttonTitle:
        variant === "quantityButtonsWidgetCompact"
          ? `Buy ${index + 1}`
          : index === 0
            ? "One item"
            : index === 1
              ? "Two items"
              : "Three items",
      label:
        variant === "quantityButtonsWidgetCompact" && index !== 0
          ? "-X%"
          : index === 0
            ? "Standard"
            : index === 1
              ? "Best seller"
              : "Best deal",
      badge: index === 1 ? "Most popular" : undefined,
      subtitle:
        variant !== "quantityButtonsWidgetCompact" && index !== 0
          ? "$X.XX/item"
          : undefined,
      showCompareAtPrice: index !== 0,
      image: undefined,
    })),
  };
};

export const shrinkQuantityButtonsForPreview = (
  buttonStyles: RecursivePartial<QuantityButtonsStyles> | undefined,
  variant: QuantityButtonsVariant
): RecursivePartial<QuantityButtonsStyles> => {
  return {
    ...buttonStyles,
    typography: {
      ...buttonStyles?.typography,
      buttonTitle: {
        fontSize:
          variant === "quantityButtonsWidgetCompact" ? "7.2px" : "8.4px",
        fontStyle: "medium",
      },
      label: {
        fontSize: variant === "quantityButtonsWidgetCompact" ? "6px" : "6px",
        fontStyle: "light",
      },
      badge: {
        fontSize: "7.2px",
        fontStyle: "medium",
      },
      subtitle: {
        fontSize: "7.2px",
        fontStyle: "light",
      },
      comparePrice: {
        fontSize: variant === "quantityButtonsWidgetCompact" ? "6px" : "6.6px",
        fontStyle: "light",
      },
      price: {
        fontSize:
          variant === "quantityButtonsWidgetCompact" ? "7.2px" : "8.4px",
        fontStyle: "medium",
      },
      sectionTitle: {
        fontSize: "7.7px",
        fontStyle: "medium",
      },
      freeGiftLabel: {
        fontSize: "5.5px",
        fontStyle: "light",
      },
      freeShippingLabel: {
        fontSize: "5.5px",
        fontStyle: "light",
      },
    },
    appearance: {
      ...buttonStyles?.appearance,
      cornerRadius: variant === "quantityButtonsWidgetCompact" ? 6 : 5.5,
      spacing: variant === "quantityButtonsWidgetCompact" ? "7px" : "6.6px",
    },
  };
};
export const generateQuantityButtonWidgetForPreview = (
  widget: QuantityButtonsWidgetDto,
  updateStyles?: boolean
): QuantityButtonsWidgetDto => {
  return {
    ...widget,
    config: {
      ...widget.config,
      desktop: {
        ...widget.config.desktop,
        ...(updateStyles && {
          styles: shrinkQuantityButtonsForPreview(
            widget.config.desktop?.styles,
            widget.config.desktop?.variant || "quantityButtonsWidgetClassic"
          ),
        }),
        variables: generatePlaceholderPreviewQuantityVariables(
          widget.config.desktop?.variables,
          widget.config.desktop?.variant || "quantityButtonsWidgetClassic"
        ),
      },
      mobile: {
        ...widget.config.desktop,
        variables: generatePlaceholderPreviewQuantityVariables(
          widget.config.desktop?.variables,
          widget.config.desktop?.variant || "quantityButtonsWidgetClassic"
        ),
        ...(updateStyles && {
          styles: shrinkQuantityButtonsForPreview(
            widget.config.desktop?.styles,
            widget.config.desktop?.variant || "quantityButtonsWidgetClassic"
          ),
        }),
      },
    },
  };
};

export const shrinkProgressBarForPreview = (
  buttonStyles: RecursivePartial<ProgressBarStyles> | undefined,
  useDefaultColors?: boolean
): RecursivePartial<ProgressBarStyles> => {
  return {
    ...buttonStyles,
    typography: {
      ...buttonStyles?.typography,
      breakpointText: {
        ...buttonStyles?.typography?.breakpointText,
        fontSize: "5.5px",
        fontStyle: "light",
      },
      dynamicProgressText: {
        ...buttonStyles?.typography?.dynamicProgressText,
        fontSize: "7.7px",
        fontStyle: "regular",
      },
    },
    breakpoint: useDefaultColors
      ? {
          // Widget library default colors
          activeColor: "#000",
          inactiveColor: "#CCCCCC",
          activeIconColor: "#FFF",
          inactiveIconColor: "#000",
          size: 14.29,
          iconSize: 6.4,
        }
      : {
          ...buttonStyles?.breakpoint,
          // Preserve user's colors, only scale sizes
          size: 14.29,
          iconSize: 6.4,
        },
    bar: useDefaultColors
      ? {
          // Widget library default colors
          thickness: 5.49,
          cornerRadius: 4.46,
          inactiveColor: "#CCCCCC",
          activeColor: "#000",
        }
      : {
          ...buttonStyles?.bar,
          // Preserve user's colors, only scale sizes
          thickness: 5.49,
          cornerRadius: 4.46,
        },
  };
};

export const generatePlaceholderPreviewProgressBarVariables = (
  barVariables: RecursivePartial<ProgressBarVariables> | undefined
): RecursivePartial<ProgressBarVariables> => {
  return {
    ...barVariables,
    textReplacements: {
      ...barVariables?.textReplacements,
      tierVariables: {
        "1": {
          tooltipTemplate: "Free shipping",
          activeIconId: "check",
          activeBackgroundIconId: "circle",
          tooltipEnabled: true,
        },
        "2": {
          tooltipTemplate: "X% off",
          inactiveIconId: "lockClosed",
          inactiveBackgroundIconId: "circle",
          tooltipEnabled: true,
          buyMoreTemplate: "Buy X more items to get X% off!",
        },
        "3": {
          tooltipTemplate: "Gift with purchase",
          inactiveIconId: "lockClosed",
          inactiveBackgroundIconId: "circle",
          tooltipEnabled: true,
          buyMoreTemplate: "Buy X more items to get X% off!",
        },
      },
    },
  };
};

export const generateProgressBarWidgetForPreview = (
  widget: ProgressBarWidgetDto,
  updateStyles?: boolean,
  useDefaultColors?: boolean
): ProgressBarWidgetDto => {
  return {
    ...widget,
    config: {
      ...widget.config,
      desktop: {
        ...widget.config.desktop,
        ...(updateStyles && {
          styles: shrinkProgressBarForPreview(
            widget.config.desktop?.styles,
            useDefaultColors
          ),
        }),
        variables: generatePlaceholderPreviewProgressBarVariables(
          widget.config.desktop?.variables
        ),
      },
      mobile: {
        ...widget.config.desktop,
        variables: generatePlaceholderPreviewProgressBarVariables(
          widget.config.desktop?.variables
        ),
        ...(updateStyles && {
          styles: shrinkProgressBarForPreview(
            widget.config.desktop?.styles,
            useDefaultColors
          ),
        }),
      },
    },
  };
};

export const generatePlaceholderPreviewMessageBoxVariables = (
  boxVariables: RecursivePartial<MessageBoxVariables> | undefined
): RecursivePartial<MessageBoxVariables> => {
  return {
    ...boxVariables,
    textReplacements: {
      ...boxVariables?.textReplacements,
      body: "Get X% off for a limited time.",
      title: "Special offer",
    },
  };
};

export const shrinkMessageBoxForPreview = (
  buttonStyles: RecursivePartial<MessageBoxStyles> | undefined,
  useDefaultColors?: boolean
): RecursivePartial<MessageBoxStyles> => {
  return {
    ...buttonStyles,
    placement: "top-right",
    typography: {
      title: {
        fontSize: "8px",
        fontStyle: "medium",
      },
      description: {
        fontSize: "7px",
      },
    },
    ...(!useDefaultColors && {
      container: {
        backgroundColor: "#000",
        cornerRadius: "5px",
        textColor: "#FFF",
      },
    }),
  };
};
export const generateMessageBoxWidgetForPreview = (
  widget: MessageBoxWidgetDto,
  updateStyles?: boolean,
  useDefaultColors?: boolean
): MessageBoxWidgetDto => {
  return {
    ...widget,
    config: {
      ...widget.config,
      desktop: {
        ...widget.config.desktop,
        ...(updateStyles && {
          styles: shrinkMessageBoxForPreview(
            widget.config.desktop?.styles,
            useDefaultColors
          ),
        }),
        variables: generatePlaceholderPreviewMessageBoxVariables(
          widget.config.desktop?.variables
        ),
      },
      mobile: {
        ...widget.config.desktop,
        variables: generatePlaceholderPreviewMessageBoxVariables(
          widget.config.desktop?.variables
        ),
        ...(updateStyles && {
          styles: shrinkMessageBoxForPreview(
            widget.config.desktop?.styles,
            useDefaultColors
          ),
        }),
      },
    },
  };
};
